/**
 * Plain words for permission codes on the Permission matrix (RL-1793, gap
 * #154): staff see the area by its sidebar name and a permission by its
 * catalogue description, never `patient_messages:manage`.
 */
const AREA_NAMES: Record<string, string> = {
  ageing: 'Outstanding / ageing',
  audit_log: 'Audit log',
  books: 'Books & payouts',
  branches: 'Branches & rooms',
  calendar: 'Calendar & bookings',
  claims: 'Panel claims',
  clinic_chat: 'Patient chat',
  consent_templates: 'Consent templates',
  data_security: 'Data & security',
  discounts: 'Discount schemes',
  doctor_inbox: 'Doctors’ inbox',
  encounters: 'Consultations',
  go_live: 'Go-live',
  invoices: 'Monthly invoices',
  knock_off: 'Knock-off',
  lab_results: 'Lab results',
  medicals: 'Medicals & documents',
  mothers_children: 'Mothers & children',
  my_patients: 'My patients today',
  panels: 'Panels & contracts',
  permissions: 'Permission matrix',
  queue: 'Queue & arrivals',
  report_dispatch: 'Report sending',
  results: 'Results to review',
  revenue: 'Doctor revenue',
  rosters: 'Doctors & rosters',
  safety: 'Safety & public health',
  schedules: 'Weekly hours',
  services: 'Services & prices',
  sessions: 'Staff sessions',
  staff: 'Staff & roles',
  stock: 'Stock & expiry',
  teleconsults: 'Video consults',
  waiting_room: 'Waiting-room screen',
  wellbeing_access: 'Wellbeing access',
  wellbeing_sessions: 'Wellbeing sessions',
};

const sentence = (key: string) =>
  key.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());

/** The area (the part before ":") by the name staff know from the sidebar. */
export function areaName(area: string): string {
  return AREA_NAMES[area] ?? sentence(area);
}

/**
 * A permission in words: its catalogue description, else "<Area> · <action>"
 * (e.g. `knock_off:manage` → "Knock-off · manage").
 */
export function permissionWords(
  key: string,
  catalogue: readonly { key: string; description: string | null }[] = [],
): string {
  const description = catalogue.find((p) => p.key === key)?.description;
  if (description) return description;
  const [area, action = ''] = key.split(':');
  return action
    ? `${areaName(area)} · ${action.replaceAll('_', ' ')}`
    : areaName(area);
}
